import React, { useEffect, useState, useCallback } from 'react';
import {
    Column,
    Text,
    Loader,
    Alert,
    Button,
    Row,
    Table,
    TableRow,
    TableCell,
    Select,
    SelectOption,
    Input,
    Tile,
    Icon,
    Pagination,
    Checkbox,
} from '@amzn/meridian';
import arrowsUpAndDownTokens from '@amzn/meridian-tokens/base/icon/arrows-up-and-down';
import arrowUpLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-up-large';
import arrowDownLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-down-large';
import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import useDuckDB from '../hooks/useDuckDB';

interface DataViewerProps {
    fileUrl: string;
    fileName: string;
    onClose: () => void;
}

interface SortConfig {
    column: string;
    direction: 'ascending' | 'descending';
}

interface AggregationResult {
    name: string;
    data: Record<string, unknown>[] | null;
    isLoading: boolean;
    error: string | null;
}

const PAGE_SIZE_OPTIONS = [
    { value: '20', label: '20 rows' },
    { value: '50', label: '50 rows' },
    { value: '100', label: '100 rows' },
    { value: '250', label: '250 rows' },
];

/**
 * Format bytes to human-readable string
 */
const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Check if a number looks like a timestamp (Unix epoch in milliseconds or seconds)
 */
const isLikelyTimestamp = (num: number): boolean => {
    // Unix timestamps in milliseconds: roughly 1970-2100
    const msMin = 0; // Jan 1, 1970
    const msMax = 4102444800000; // Jan 1, 2100
    // Unix timestamps in seconds
    const secMin = 0;
    const secMax = 4102444800; // Jan 1, 2100
    
    return (num >= msMin && num <= msMax && num > 1e11) || // milliseconds (> year 1973)
           (num >= secMin && num <= secMax && num > 1e8 && num < 1e11); // seconds
};

/**
 * Format a timestamp to human-readable date string (UTC to match DuckDB storage)
 */
const formatTimestamp = (num: number): string => {
    // If it looks like seconds (smaller number), convert to ms
    const ms = num < 1e11 ? num * 1000 : num;
    const date = new Date(ms);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
        return String(num);
    }
    
    // Format: YYYY-MM-DD HH:MM:SS (using UTC to match DuckDB date storage)
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    
    // Always show full datetime with time component
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Format cell values for display in the table.
 * - Dates/timestamps are formatted as human-readable strings
 * - Numbers are rounded to 2 decimal places
 * - Objects are JSON stringified
 * - Long strings are truncated
 */
const formatCellValue = (value: unknown): string => {
    if (value === null) return 'null';
    if (value === undefined) return '';
    
    // Handle Date objects
    if (value instanceof Date) {
        return formatTimestamp(value.getTime());
    }
    
    // Handle numbers
    if (typeof value === 'number') {
        // Check if it's NaN or Infinity
        if (!isFinite(value)) {
            return String(value);
        }
        
        // Check if it looks like a timestamp
        if (isLikelyTimestamp(value)) {
            return formatTimestamp(value);
        }
        
        // Round to 2 decimal places if it has decimals
        if (!Number.isInteger(value)) {
            return value.toFixed(2);
        }
        
        // Return integer as-is
        return String(value);
    }
    
    // Handle BigInt from DuckDB
    if (typeof value === 'bigint') {
        const num = Number(value);
        if (isLikelyTimestamp(num)) {
            return formatTimestamp(num);
        }
        return String(value);
    }
    
    // Handle objects (arrays, etc.)
    if (typeof value === 'object') {
        const str = JSON.stringify(value);
        return str.length > 100 ? str.substring(0, 100) + '...' : str;
    }
    
    // Handle strings - check for ISO date format
    if (typeof value === 'string') {
        // Check for ISO date strings (e.g., "2024-01-15T10:30:00Z" or "2024-01-15")
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
        if (isoDateRegex.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return formatTimestamp(date.getTime());
            }
        }
        
        // Regular string - truncate if too long
        return value.length > 100 ? value.substring(0, 100) + '...' : value;
    }
    
    const str = String(value);
    return str.length > 100 ? str.substring(0, 100) + '...' : str;
};

const DataViewer: React.FC<DataViewerProps> = ({ fileUrl, fileName, onClose }) => {
    const { isReady, isLoading, loadingProgress, error, rowCount, columns, loadCsv, executeQuery } = useDuckDB();
    const [data, setData] = useState<Record<string, unknown>[]>([]);
    const [queryError, setQueryError] = useState<string | null>(null);
    const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    
    // Sorting state
    const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', direction: 'ascending' });
    
    // Per-column filter state
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
    const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});
    const [filteredRowCount, setFilteredRowCount] = useState<number | null>(null);
    const [columnUniqueValues, setColumnUniqueValues] = useState<Record<string, string[]>>({});
    const [filterSearchQueries, setFilterSearchQueries] = useState<Record<string, string>>({});
    
    // Column analysis sidebar state
    const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
    const [columnAnalysis, setColumnAnalysis] = useState<AggregationResult[]>([]);
    
    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const [columnPickerSearch, setColumnPickerSearch] = useState('');

    // Load CSV when DuckDB is ready and we have a new URL
    useEffect(() => {
        if (isReady && fileUrl && fileUrl !== loadedUrl) {
            loadCsvData();
        }
    }, [isReady, fileUrl, loadedUrl]);

    const loadCsvData = () => {
        setQueryError(null);
        setData([]);
        setCurrentPage(1);
        setSortConfig({ column: '', direction: 'ascending' });
        setColumnFilters({});
        setAppliedFilters({});
        setFilteredRowCount(null);
        setSelectedColumn(null);
        setColumnUniqueValues({});
        setFilterSearchQueries({});
        setVisibleColumns(new Set());
        setShowColumnPicker(false);
        
        loadCsv(fileUrl)
            .then(() => executeQuery(`SELECT * FROM data LIMIT ${pageSize}`))
            .then(results => {
                setData(results);
                setLoadedUrl(fileUrl);
            })
            .catch(err => {
                setQueryError(err instanceof Error ? err.message : 'Failed to load data');
            });
    };

    // Load unique values for all columns when columns are available
    useEffect(() => {
        if (loadedUrl && columns.length > 0 && Object.keys(columnUniqueValues).length === 0) {
            loadUniqueValues();
        }
    }, [loadedUrl, columns]);

    // Initialize visible columns when columns are loaded
    useEffect(() => {
        if (columns.length > 0 && visibleColumns.size === 0) {
            setVisibleColumns(new Set(columns));
        }
    }, [columns]);

    const loadUniqueValues = () => {
        columns.forEach(col => {
            // Get top 100 unique raw values - keep original for filtering
            const query = `SELECT DISTINCT "${col}" as val FROM data WHERE "${col}" IS NOT NULL ORDER BY "${col}" LIMIT 100`;
            executeQuery(query)
                .then(results => {
                    const values = results.map(r => String(r.val)).filter(v => v.length > 0);
                    setColumnUniqueValues(prev => ({ ...prev, [col]: values }));
                })
                .catch(() => {
                    // Silently fail for individual columns
                });
        });
    };

    // Search for column values matching a search query (for filter dropdowns)
    const searchColumnValues = (col: string, searchQuery: string): void => {
        if (!searchQuery || searchQuery.length < 1) return;
        
        const escapedQuery = searchQuery.replace(/'/g, "''");
        const query = `SELECT DISTINCT "${col}" as val FROM data WHERE "${col}" IS NOT NULL AND CAST("${col}" AS VARCHAR) ILIKE '%${escapedQuery}%' ORDER BY "${col}" LIMIT 100`;
        
        executeQuery(query)
            .then(results => {
                const values = results.map(r => String(r.val)).filter(v => v.length > 0);
                setColumnUniqueValues(prev => ({ ...prev, [col]: values }));
            })
            .catch(() => {
                // Silently fail
            });
    };

    // Convert timestamp (ms or s) to SQL DATE string (UTC - matches DuckDB storage)
    const timestampToDateString = (num: number): string => {
        const ms = num < 1e11 ? num * 1000 : num;
        const date = new Date(ms);
        // Use UTC to match DuckDB's date storage (dates are stored in UTC)
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Build WHERE clause from active filters
    const buildWhereClause = (filters: Record<string, string>): string => {
        const activeFilters = Object.entries(filters).filter(([_, value]) => value.trim() !== '');
        if (activeFilters.length === 0) return '';
        
        const conditions = activeFilters.map(([col, val]) => {
            const escapedVal = val.replace(/'/g, "''");
            const numVal = parseFloat(val);
            
            // Check if it's a numeric value that looks like a timestamp
            if (!isNaN(numVal) && val.trim() !== '' && String(numVal) === val.trim()) {
                if (isLikelyTimestamp(numVal)) {
                    // Convert timestamp to date string for DATE column comparison (using local timezone)
                    const dateStr = timestampToDateString(numVal);
                    return `"${col}" = DATE '${dateStr}'`;
                }
                // Regular numeric comparison
                return `"${col}" = ${numVal}`;
            }
            // For string values that look like dates (YYYY-MM-DD format), compare as date
            const dateMatch = /^\d{4}-\d{2}-\d{2}$/.test(val);
            if (dateMatch) {
                return `"${col}" = DATE '${escapedVal}'`;
            }
            // Default: string comparison
            return `"${col}" = '${escapedVal}'`;
        });
        return ' WHERE ' + conditions.join(' AND ');
    };

    // Load page when pagination, sort, or filter changes
    const loadPage = (page: number, size: number, newSort?: SortConfig, newFilters?: Record<string, string>) => {
        if (!loadedUrl) return;
        
        setIsLoadingPage(true);
        const offset = (page - 1) * size;
        const sort = newSort ?? sortConfig;
        const filters = newFilters !== undefined ? newFilters : appliedFilters;
        
        let query = 'SELECT * FROM data';
        query += buildWhereClause(filters);
        if (sort.column) {
            const sqlDir = sort.direction === 'ascending' ? 'ASC' : 'DESC';
            query += ` ORDER BY "${sort.column}" ${sqlDir}`;
        }
        query += ` LIMIT ${size} OFFSET ${offset}`;
        
        executeQuery(query)
            .then(results => {
                setData(results);
                setCurrentPage(page);
            })
            .catch(err => {
                setQueryError(err instanceof Error ? err.message : 'Failed to load page');
            })
            .finally(() => {
                setIsLoadingPage(false);
            });
    };

    const handlePageSizeChange = (newSize: string) => {
        const size = parseInt(newSize, 10);
        setPageSize(size);
        loadPage(1, size);
    };

    // Handle custom sort click
    const handleSortClick = (column: string) => {
        let newDirection: 'ascending' | 'descending' = 'ascending';
        if (sortConfig.column === column) {
            // Toggle direction if same column
            newDirection = sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        }
        const newSort: SortConfig = { column, direction: newDirection };
        setSortConfig(newSort);
        loadPage(1, pageSize, newSort);
    };

    // Handle column header click - open sidebar with analysis
    const handleColumnClick = (column: string) => {
        if (selectedColumn === column) {
            setSelectedColumn(null);
            setColumnAnalysis([]);
            return;
        }
        
        setSelectedColumn(column);
        runColumnAnalysis(column);
    };

    // Run analysis queries for a specific column
    const runColumnAnalysis = (column: string) => {
        const queries: Array<{ name: string; query: string }> = [
            { 
                name: 'Distinct Values', 
                query: `SELECT COUNT(DISTINCT "${column}") as distinct_count FROM data`
            },
            {
                name: 'Null Count',
                query: `SELECT COUNT(*) as null_count FROM data WHERE "${column}" IS NULL`
            },
            {
                name: 'Numeric Summary',
                query: `SELECT 
                    MIN("${column}") as min_value,
                    MAX("${column}") as max_value,
                    AVG(TRY_CAST("${column}" AS DOUBLE)) as avg_value,
                    MEDIAN(TRY_CAST("${column}" AS DOUBLE)) as median_value
                FROM data`
            },
            { 
                name: 'Top 10 Values', 
                query: `SELECT "${column}" as value, COUNT(*) as count FROM data GROUP BY "${column}" ORDER BY count DESC LIMIT 10`
            }
        ];
        
        const initialResults: AggregationResult[] = queries.map(q => ({
            name: q.name,
            data: null,
            isLoading: true,
            error: null
        }));
        
        setColumnAnalysis(initialResults);
        
        queries.forEach((q, idx) => {
            executeQuery(q.query)
                .then(data => {
                    setColumnAnalysis(prev => {
                        const updated = [...prev];
                        updated[idx] = { ...updated[idx], data, isLoading: false };
                        return updated;
                    });
                })
                .catch(err => {
                    setColumnAnalysis(prev => {
                        const updated = [...prev];
                        updated[idx] = { ...updated[idx], error: err instanceof Error ? err.message : 'Query failed', isLoading: false };
                        return updated;
                    });
                });
        });
    };

    // Handle per-column filter change
    const handleFilterChange = (column: string, value: string) => {
        setColumnFilters(prev => ({ ...prev, [column]: value }));
    };

    // Apply filters when Enter is pressed or on blur
    const handleApplyFilters = () => {
        setAppliedFilters(columnFilters);
        
        // Get filtered count
        const whereClause = buildWhereClause(columnFilters);
        if (whereClause) {
            const countQuery = `SELECT COUNT(*) as count FROM data${whereClause}`;
            executeQuery(countQuery)
                .then(result => {
                    const count = result[0]?.count as number ?? 0;
                    setFilteredRowCount(count);
                })
                .catch(() => {});
        } else {
            setFilteredRowCount(null);
        }
        
        loadPage(1, pageSize, sortConfig, columnFilters);
    };

    // Clear all filters
    const handleClearAllFilters = () => {
        setColumnFilters({});
        setAppliedFilters({});
        setFilteredRowCount(null);
        loadPage(1, pageSize, sortConfig, {});
    };

    // Check if any filters are active
    const hasActiveFilters = Object.values(appliedFilters).some(v => v.trim() !== '');

    const effectiveRowCount = filteredRowCount ?? rowCount;
    const totalPages = effectiveRowCount ? Math.ceil(effectiveRowCount / pageSize) : 0;

    // Get displayed columns based on visibility settings
    const displayedColumns = columns.filter(col => visibleColumns.has(col));
    const allColumnsVisible = visibleColumns.size === columns.length;

    // Toggle column visibility
    const toggleColumnVisibility = (col: string) => {
        setVisibleColumns(prev => {
            const newSet = new Set(prev);
            if (newSet.has(col)) {
                newSet.delete(col);
            } else {
                newSet.add(col);
            }
            return newSet;
        });
    };

    // Select all columns
    const selectAllColumns = () => {
        setVisibleColumns(new Set(columns));
    };

    // Deselect all columns
    const deselectAllColumns = () => {
        setVisibleColumns(new Set());
    };

    // Filter columns based on search
    const filteredColumns = columns.filter(col => 
        col.toLowerCase().includes(columnPickerSearch.toLowerCase())
    );

    // Show DuckDB initialization state
    if (!isReady) {
        return (
            <Column spacing="400" spacingInset="400">
                <Row>
                    <Button type="tertiary" size="small" onClick={onClose}>← Back</Button>
                </Row>
                <Column alignmentHorizontal="center" spacing="300">
                    <Loader size="large" />
                    <Text>Initializing DuckDB...</Text>
                </Column>
            </Column>
        );
    }

    // Show loading state with progress
    if (isLoading) {
        const { stage, bytesLoaded, bytesTotal, percent } = loadingProgress;
        const stageLabels = {
            idle: 'Preparing...',
            downloading: 'Downloading...',
            parsing: 'Parsing CSV...',
            indexing: 'Creating index...',
            complete: 'Complete!',
        };
        
        return (
            <Column spacing="400" spacingInset="400">
                <Row>
                    <Button type="tertiary" size="small" onClick={onClose}>← Back</Button>
                </Row>
                <Column alignmentHorizontal="center" spacing="300">
                    <Loader size="large" />
                    <Text type="h300">{stageLabels[stage]}</Text>
                    <Text type="b100" color="secondary">{fileName}</Text>
                    
                    {stage === 'downloading' && (
                        <Column alignmentHorizontal="center" spacing="100">
                            {percent !== null ? (
                                <>
                                    <div style={{ 
                                        width: '200px', 
                                        height: '8px', 
                                        backgroundColor: '#e0e0e0', 
                                        borderRadius: '4px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{ 
                                            width: `${percent}%`, 
                                            height: '100%', 
                                            backgroundColor: '#0073bb',
                                            transition: 'width 0.2s ease'
                                        }} />
                                    </div>
                                    <Text type="b100" color="secondary">
                                        {formatBytes(bytesLoaded)} / {bytesTotal ? formatBytes(bytesTotal) : '?'} ({percent}%)
                                    </Text>
                                </>
                            ) : (
                                <Text type="b100" color="secondary">
                                    {formatBytes(bytesLoaded)} downloaded
                                </Text>
                            )}
                        </Column>
                    )}
                </Column>
            </Column>
        );
    }

    // Show error state
    if (error || queryError) {
        const errorMessage = error || queryError || 'Unknown error';
        const isNetworkError = errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('CORS');
        const isParseError = errorMessage.includes('parse') || errorMessage.includes('CSV') || errorMessage.includes('syntax');
        
        return (
            <Column spacing="400" spacingInset="400">
                <Row>
                    <Button type="tertiary" size="small" onClick={onClose}>← Back</Button>
                </Row>
                <Alert type="error" title="Error loading data">
                    <Column spacing="200">
                        <Text>{errorMessage}</Text>
                        {isNetworkError && (
                            <Text type="b100" color="secondary">
                                This may be a network or CORS issue. Check that the file is accessible and CORS is configured on the S3 bucket.
                            </Text>
                        )}
                        {isParseError && (
                            <Text type="b100" color="secondary">
                                The file may not be valid CSV format or may contain encoding issues.
                            </Text>
                        )}
                    </Column>
                </Alert>
                <Row spacing="300">
                    <Button onClick={() => { setLoadedUrl(null); setQueryError(null); }}>
                        Retry
                    </Button>
                    <Button type="tertiary" onClick={onClose}>
                        Close
                    </Button>
                </Row>
            </Column>
        );
    }

    // Column Analysis Sidebar (right side with animation)
    const renderSidebar = () => {
        return (
            <div style={{ 
                position: 'fixed',
                top: 0,
                right: 0,
                width: '360px',
                height: '100vh',
                borderLeft: '1px solid #d5dbdb',
                backgroundColor: '#ffffff',
                overflowY: 'auto',
                boxShadow: selectedColumn ? '-4px 0 12px rgba(0, 0, 0, 0.15)' : 'none',
                transform: selectedColumn ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                zIndex: 100
            }}>
                <Column spacing="400" spacingInset="400">
                    {/* Header */}
                    <Row widths={['fill', 'fit']} alignmentVertical="center">
                        <Column spacing="100">
                            <Text type="h300">Column Analysis</Text>
                            <Text type="b200" color="primary">{selectedColumn}</Text>
                        </Column>
                        <Button 
                            type="tertiary" 
                            size="small" 
                            onClick={() => { setSelectedColumn(null); setColumnAnalysis([]); }}
                        >
                            Close
                        </Button>
                    </Row>
                    
                    {/* Analysis Results */}
                    {selectedColumn && columnAnalysis.map((result, idx) => (
                        <Tile key={idx} spacingInset="300">
                            <Column spacing="200">
                                <Text type="h100">{result.name}</Text>
                                {result.isLoading ? (
                                    <Row spacing="200" alignmentVertical="center">
                                        <Loader size="small" />
                                        <Text type="b100" color="secondary">Calculating...</Text>
                                    </Row>
                                ) : result.error ? (
                                    <Alert type="warning" size="small">
                                        {result.error}
                                    </Alert>
                                ) : result.data && result.data.length > 0 ? (
                                    result.data.length === 1 ? (
                                        // Single row result - show as key-value pairs
                                        <Column spacing="100">
                                            {Object.entries(result.data[0]).map(([key, value]) => (
                                                <Row key={key} widths={['fill', 'fit']}>
                                                    <Text type="b100" color="secondary">{key.replace(/_/g, ' ')}</Text>
                                                    <Text type="b200">{formatCellValue(value)}</Text>
                                                </Row>
                                            ))}
                                        </Column>
                                    ) : (
                                        // Multiple rows - show as mini table
                                        <Table headerRows={1} showDividers={true} spacing="small">
                                            <TableRow>
                                                {Object.keys(result.data[0]).map(key => (
                                                    <TableCell key={key}>
                                                        <Text type="b100">{key}</Text>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            {result.data.map((row, rowIdx) => (
                                                <TableRow key={rowIdx}>
                                                    {Object.values(row).map((val, colIdx) => (
                                                        <TableCell key={colIdx}>
                                                            <Text type="b100">{formatCellValue(val)}</Text>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </Table>
                                    )
                                ) : (
                                    <Text type="b100" color="secondary">No data available</Text>
                                )}
                            </Column>
                        </Tile>
                    ))}
                </Column>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', width: '100%', height: '100%' }}>
            {/* Column Analysis Sidebar */}
            {renderSidebar()}
            
            {/* Main Content */}
            <Column spacing="400" spacingInset="400" width="100%">
                {/* Fix z-index for Select dropdowns */}
                <style>{`
                    [class*="meridian"][class*="dropdown"],
                    [class*="meridian"][class*="menu"],
                    [class*="meridian"][class*="select"] [role="listbox"],
                    [class*="meridian"][class*="overlay"],
                    [class*="meridian"][class*="popover"] {
                        z-index: 1000 !important;
                    }
                `}</style>
                
                {/* Header */}
                <Column spacing="100">
                    <Text type="h200">{fileName}</Text>
                    <Text type="b100" color="secondary">
                        {rowCount !== null ? `${rowCount.toLocaleString()} total rows` : ''} 
                        {columns.length > 0 ? ` • ${columns.length} columns` : ''}
                        {' • Click column header for analysis'}
                    </Text>
                </Column>

                {/* Top controls row: filters indicator, page size, and column picker */}
                <Row widths={['fill', 'fit', 'fit']} alignmentVertical="center">
                    <Row spacing="200" alignmentVertical="center">
                        {hasActiveFilters && (
                            <>
                                <Text type="b100" color="secondary">
                                    🔍 Filters active ({filteredRowCount?.toLocaleString() ?? '...'} of {rowCount?.toLocaleString()} rows)
                                </Text>
                                <Button type="tertiary" size="small" onClick={handleClearAllFilters}>
                                    Clear All Filters
                                </Button>
                            </>
                        )}
                        {!hasActiveFilters && effectiveRowCount !== null && (
                            <Text type="b100" color="secondary">
                                {effectiveRowCount.toLocaleString()} rows
                            </Text>
                        )}
                    </Row>
                    <Select
                        value={String(pageSize)}
                        onChange={handlePageSizeChange}
                        size="small"
                        width={120}
                    >
                        {PAGE_SIZE_OPTIONS.map(opt => (
                            <SelectOption key={opt.value} value={opt.value} label={opt.label} />
                        ))}
                    </Select>
                    {/* Column visibility picker */}
                    <div style={{ position: 'relative' }}>
                        <Button
                            type="icon"
                            size="small"
                            onClick={() => setShowColumnPicker(!showColumnPicker)}
                        >
                            <Icon tokens={cogTokens} />
                        </Button>
                        {showColumnPicker && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '4px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #d5dbdb',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                zIndex: 1001,
                                minWidth: '280px',
                                maxHeight: '450px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Column spacing="200" spacingInset="300">
                                    <Row widths={['fill', 'fit']} alignmentVertical="center">
                                        <Text type="h100">Visible Columns</Text>
                                        <Button 
                                            type="icon" 
                                            size="small"
                                            onClick={() => setShowColumnPicker(false)}
                                        >
                                            ✕
                                        </Button>
                                    </Row>
                                    <Input
                                        value={columnPickerSearch}
                                        onChange={setColumnPickerSearch}
                                        placeholder="Search columns..."
                                        size="small"
                                    />
                                    <Row spacing="200">
                                        <Button type="tertiary" size="small" onClick={selectAllColumns}>
                                            Select All
                                        </Button>
                                        <Button type="tertiary" size="small" onClick={deselectAllColumns}>
                                            Deselect All
                                        </Button>
                                    </Row>
                                    <Text type="b100" color="secondary">
                                        {visibleColumns.size} of {columns.length} columns visible
                                    </Text>
                                </Column>
                                <div style={{ borderTop: '1px solid #e0e0e0' }} />
                                <div style={{ overflowY: 'auto', maxHeight: '280px', padding: '12px' }}>
                                    <Column spacing="100">
                                        {filteredColumns.length > 0 ? (
                                            filteredColumns.map(col => (
                                                <Checkbox
                                                    key={col}
                                                    checked={visibleColumns.has(col)}
                                                    onChange={() => toggleColumnVisibility(col)}
                                                >
                                                    {col}
                                                </Checkbox>
                                            ))
                                        ) : (
                                            <Text type="b100" color="secondary">No columns match "{columnPickerSearch}"</Text>
                                        )}
                                    </Column>
                                </div>
                            </div>
                        )}
                    </div>
                </Row>

                {/* Data table */}
                <Column spacing="200">
                    {isLoadingPage && (
                        <Row alignmentHorizontal="center">
                            <Loader size="small" />
                            <Text type="b100" color="secondary">Loading page...</Text>
                        </Row>
                    )}
                    {data.length > 0 && columns.length > 0 ? (
                        <div className="compact-data-table" style={{ overflowX: 'auto' }}>
                            <style>{`
                                .compact-data-table table {
                                    table-layout: fixed !important;
                                    min-width: 100% !important;
                                    width: max-content !important;
                                }
                                .compact-data-table th,
                                .compact-data-table td {
                                    min-width: 120px !important;
                                    max-width: 300px !important;
                                }
                                .compact-data-table td {
                                    overflow: hidden !important;
                                    text-overflow: ellipsis !important;
                                    white-space: nowrap !important;
                                }
                                .compact-data-table th {
                                    white-space: normal !important;
                                    vertical-align: top !important;
                                }
                                .column-header-clickable:hover {
                                    background-color: #e8f4fc !important;
                                }
                                .column-header-selected {
                                    background-color: #d4e8f7 !important;
                                }
                                .sort-icon {
                                    display: inline-flex;
                                    align-items: center;
                                    justify-content: center;
                                    margin-left: 4px;
                                    cursor: pointer;
                                    border-radius: 2px;
                                }
                                .sort-icon:hover {
                                    background-color: rgba(0, 115, 187, 0.1);
                                }
                            `}</style>
                            <Table
                                headerRows={1}
                                showDividers={true}
                                spacing="small"
                            >
                                {/* Combined header row with column names, sort icons, and filters */}
                                <TableRow>
                                    {displayedColumns.map((col) => {
                                        const isActive = sortConfig.column === col;
                                        const isAsc = sortConfig.direction === 'ascending';
                                        return (
                                            <TableCell key={col}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span 
                                                            onClick={() => handleColumnClick(col)}
                                                            className={`column-header-clickable ${selectedColumn === col ? 'column-header-selected' : ''}`}
                                                            style={{ 
                                                                cursor: 'pointer', 
                                                                userSelect: 'none',
                                                                padding: '2px 4px',
                                                                borderRadius: '2px',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {col}
                                                        </span>
                                                        <Button
                                                            type="icon"
                                                            size="small"
                                                            onClick={() => handleSortClick(col)}
                                                        >
                                                            {isActive ? (
                                                                isAsc ? (
                                                                    <Icon tokens={arrowUpLargeTokens} color="primary" />
                                                                ) : (
                                                                    <Icon tokens={arrowDownLargeTokens} color="primary" />
                                                                )
                                                            ) : (
                                                                <Icon tokens={arrowsUpAndDownTokens} />
                                                            )}
                                                        </Button>
                                                    </div>
                                                    {(() => {
                                                        const searchQuery = filterSearchQueries[col] || '';
                                                        const searchRegExp = new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
                                                        const allOptions = columnUniqueValues[col] || [];
                                                        const matchedOptions = allOptions.filter(
                                                            opt => !searchQuery || searchRegExp.test(opt)
                                                        );
                                                        return (
                                                            <Select
                                                                value={columnFilters[col] || ''}
                                                                onChange={(value: string) => {
                                                                    handleFilterChange(col, value);
                                                                    const newFilters = { ...columnFilters, [col]: value };
                                                                    setAppliedFilters(newFilters);
                                                                    const whereClause = buildWhereClause(newFilters);
                                                                    if (whereClause) {
                                                                        executeQuery(`SELECT COUNT(*) as count FROM data${whereClause}`)
                                                                            .then(result => setFilteredRowCount(result[0]?.count as number ?? 0))
                                                                            .catch(() => {});
                                                                    } else {
                                                                        setFilteredRowCount(null);
                                                                    }
                                                                    loadPage(1, pageSize, sortConfig, newFilters);
                                                                }}
                                                                size="small"
                                                                placeholder="All"
                                                                width="100%"
                                                                searchQuery={searchQuery}
                                                                onSearch={(query: string) => {
                                                                    setFilterSearchQueries(prev => ({ ...prev, [col]: query }));
                                                                    // Dynamically search the database for matching values
                                                                    if (query.length >= 1) {
                                                                        searchColumnValues(col, query);
                                                                    } else {
                                                                        // Reset to initial values when search is cleared
                                                                        const resetQuery = `SELECT DISTINCT "${col}" as val FROM data WHERE "${col}" IS NOT NULL ORDER BY "${col}" LIMIT 100`;
                                                                        executeQuery(resetQuery)
                                                                            .then(results => {
                                                                                const values = results.map(r => String(r.val)).filter(v => v.length > 0);
                                                                                setColumnUniqueValues(prev => ({ ...prev, [col]: values }));
                                                                            })
                                                                            .catch(() => {});
                                                                    }
                                                                }}
                                                            >
                                                                <SelectOption value="" label="All" />
                                                                {matchedOptions.map(val => {
                                                                    // Format the label for display (dates, numbers)
                                                                    // Try to parse as number first to format timestamps
                                                                    const numVal = parseFloat(val);
                                                                    let displayLabel: string;
                                                                    if (!isNaN(numVal) && val.trim() !== '' && isLikelyTimestamp(numVal)) {
                                                                        displayLabel = formatTimestamp(numVal);
                                                                    } else if (!isNaN(numVal) && val.trim() !== '' && !Number.isInteger(numVal)) {
                                                                        displayLabel = numVal.toFixed(2);
                                                                    } else {
                                                                        displayLabel = val.length > 30 ? val.substring(0, 30) + '...' : val;
                                                                    }
                                                                    return (
                                                                        <SelectOption 
                                                                            key={val} 
                                                                            value={val} 
                                                                            label={displayLabel} 
                                                                        />
                                                                    );
                                                                })}
                                                                {searchQuery && matchedOptions.length === 0 && (
                                                                    <Column alignmentVertical="center" spacing="200" spacingInset="400">
                                                                        <Text alignment="center" type="b100" color="secondary">No results</Text>
                                                                    </Column>
                                                                )}
                                                            </Select>
                                                        );
                                                    })()}
                                                </div>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                                {/* Data rows */}
                                {data.map((row, rowIndex) => (
                                    <TableRow key={rowIndex} highlightOnHover={true}>
                                        {displayedColumns.map((col) => (
                                            <TableCell key={`${rowIndex}-${col}`}>
                                                <div style={{ paddingTop: '4px' }}>
                                                    <Text type="b200">{formatCellValue(row[col])}</Text>
                                                </div>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </Table>
                        </div>
                    ) : (
                        <Text type="b100" color="secondary">No data to display</Text>
                    )}
                    
                    {/* Bottom pagination - centered */}
                    {totalPages > 1 && (
                        <Row alignmentHorizontal="center" spacingInset="300">
                            <Pagination
                                numberOfPages={totalPages}
                                currentPage={currentPage}
                                onChange={(newPage: number) => loadPage(newPage, pageSize)}
                                showSkipArrows={true}
                            />
                        </Row>
                    )}
                </Column>
            </Column>
        </div>
    );
};

export default DataViewer;
